/*
Copyright 2025 iodyne.  All rights reserved.
iodyne Proprietary/Confidental: INTERNAL USE ONLY
*/

import { defaultNS } from '@/i18n/settings';
import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next';
import ProjectIconSrc from '@/components/icons/svg/project_icon.svg';
import IodyneCloudIcon from '@/components/icons/IodyneCloudIcon';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { RedirectForAuthPopup } from '@/components/controllers/auth/ValidateAccessToken';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useActivateUserMutation } from '@/lib/datastore/slices/users';
import { OptionalConsents } from '@/lib/models/org';
import type { UserConsents } from '@/lib/models/people';
import { decodeAPIError } from '@/lib/models/errors';
import { DialogPopup, DialogPopupType } from '@/components/ui/DialogPopup';

export const Route = createFileRoute('/activate')({
  component: RouteComponent,
  validateSearch: (search) => ({
    activation_token: search.activation_token as string,
    client_id: search.client_id as string,
    org_id: search.org_id as string,
    project_name: search.project_name as string ?? "",
    org_name: search.org_name as string ?? "",
    coverart_uri: search.coverart_uri as string ?? "",
    require_update_details: search.require_update_details as boolean ?? ""
  })
})

function RouteComponent() {
    const { t } = useTranslation(defaultNS);
    const { client_id, activation_token, project_name, org_name, coverart_uri, org_id, require_update_details } = Route.useSearch();

    const need_details = require_update_details === true;
    
    const url = location.href;
    const url_params = (new URL(url)).searchParams;
    const [do_login, setDoLogin] = useState<boolean>(false);
    const [err_msg, setErrMsg] = useState<string|undefined>(undefined);
    const [password, setPassword] = useState<string>("");
    const [password_again, setPasswordAgain] = useState<string>("");
    const [services_agreement, setServicesAgreement] = useState<boolean>(false);
    const [marketing_comms, setMarketingComms] = useState<boolean>(false);
    const [telemetry, setTelemetry] = useState<boolean>(false);
    const [activating, setActivating] = useState<boolean>(false);

    const [activateUser] = useActivateUserMutation();

    const cover_art_valid = coverart_uri.startsWith(process.env.IOD_CLOUD_PORTAL_USER_ASSETS_BASE_URI!);
    var redirect_uri = url.split("/activate", 1)[0];
    if(url_params.get("project_id")) {
        redirect_uri = redirect_uri + `/project/${url_params.get("project_id")}`
    }

    const performActivation = (password?: string, consents?: UserConsents) => {
        setActivating(true);
        setErrMsg(undefined);
        activateUser({
            org_id: org_id,
            activation_token: activation_token,
            client_id: client_id,
            password: password,
            consents: consents
        }).then(({ error }) => {
            setActivating(false);
            if (error) {
                const decoded = decodeAPIError(error);
                if (decoded) {
                    setErrMsg(decoded.error_description);
                } else if ("userMessage" in error) {
                    setErrMsg(error.userMessage as string);
                }
                return;
            }
            setDoLogin(true);
        });
    };

    const finishActivation = () => {
        const optional_consents: OptionalConsents[] = [];
        if(!password) {
            setErrMsg(t("label_password_too_short"));
            return;
        }
        if(password != password_again) {
            setErrMsg(t("label_password_mismatch"));
            return;
        }
        if(!services_agreement) {
            setErrMsg(t("user_mgmt.api_errors.missing_required_consents"));
            return;
        }
        if(marketing_comms) {
            optional_consents.push(OptionalConsents.marketingComms);
        }
        if(telemetry) {
            optional_consents.push(OptionalConsents.deviceTelemetry);
        }

        performActivation(password, {
            services_agreement: services_agreement,
            optional_consents: optional_consents
        });
    };

    useEffect(() => {
        if(need_details) return;
        performActivation();
    }, [activation_token, need_details]);

    return <div className="flex flex-col w-full h-screen justify-center">
        {do_login && <RedirectForAuthPopup
            title={t("auth.activation_successful")}
            message={t("auth.not_logged_in_msg")}
            redirectURI={redirect_uri} />}
        {!do_login && <div className="flex flex-row justify-center items-center">
            <div className="flex flex-col justify-center rounded-md bg-card max-w-150">
                <div className="flex flex-col items-center space-y-5 p-8">
                    <div className="text-2xl">
                        {(!err_msg || need_details) && t("label_activate_user_welcome_title")}
                        {err_msg && !need_details && <div className="text-warning-state">{err_msg}</div>}
                    </div>
                    {project_name && <div className="flex flex-col items-center space-y-1">
                        {/* Project Details */}
                        <div>
                            {coverart_uri && cover_art_valid && <img
                                alt={project_name}
                                className="rounded-lg drop-shadow-[0_3px_3px_#222]"
                                src={coverart_uri}
                                width={100}
                                height={100} />}
                            {(!coverart_uri || !cover_art_valid) && <ProjectIconSrc height={100} />}
                        </div>
                        <div className="text-2xl">
                            {project_name}
                        </div>
                        <div>
                            {t("label_unauthenticated_project_detail", {
                                "project_name": project_name,
                                "org_name": org_name
                            })}
                        </div>
                    </div>}
                    {!project_name && need_details && <div className="flex flex-col items-center space-y-4 w-100">
                        <div className="font-medium text-muted">
                            {t("label_activate_user_welcome_message")}
                        </div>
                    </div>}
                    {/* Form */}
                    {need_details && <div className="w-50">
                        <form>
                            <div className="flex flex-col space-y-4">
                                {/* Error Message */}
                                {err_msg && <div className="text-destructive">
                                    {err_msg}
                                </div>}
                                {/* Password */}
                                <div className="flex flex-col space-y-1">
                                    <div className="">
                                        {t("label_password")}
                                    </div>
                                    <div>
                                        <Input
                                            id="password"
                                            type="password"
                                            placeholder={t("label_password")}
                                            onChange={(e) => { setPassword(e.currentTarget.value) }} />
                                    </div>
                                    <div>
                                        <Input
                                            id="repeat_password"
                                            type="password"
                                            placeholder={t("label_password_repeat")}
                                            onChange={(e) => { setPasswordAgain(e.currentTarget.value) }} />
                                    </div>
                                </div>
                                {/* Consents */}
                                <div className="flex flex-col space-y-1">
                                    <div className="">
                                        {t("label_consents")}
                                    </div>
                                    <div className="flex flex-row space-x-2">
                                        <Checkbox
                                            id="services_agreement"
                                            onCheckedChange={(e) => {setServicesAgreement(e.valueOf() as boolean)}} />
                                        <Label htmlFor="services_agreement" className="flex flex-col items-start space-y-0">
                                            <div>
                                                <a href="https://iodyne.com/cloudtos/" target="_new" className="underline font-bold">
                                                    {t("label_consent_services_agreement")}
                                                </a>
                                            </div>
                                            <div className="text-xs text-muted">(*&nbsp;{t("label_required")})</div>
                                        </Label>
                                    </div>
                                    <div className="flex flex-row space-x-2 mt-2">
                                        <Checkbox
                                            id="marketing_communications"
                                            onCheckedChange={(e) => {setMarketingComms(e.valueOf() as boolean)}} />
                                        <Label htmlFor="marketing_communications">{t("label_consent_marketing")}</Label>
                                    </div>
                                    <div className="flex flex-row space-x-2">
                                        <Checkbox
                                            id="telemetry"
                                            onCheckedChange={(e) => {setTelemetry(e.valueOf() as boolean)}}/>
                                        <Label htmlFor="telemetry">{t("label_consent_telemetry")}</Label>
                                    </div>
                                </div>
                                <div className="mt-5">
                                    {!activating && <Button
                                        onClick={(e) => { e.preventDefault(); finishActivation() }}>
                                        {t("label_finish_signup")}
                                    </Button>}
                                    {activating && <Spinner/>}
                                </div>
                            </div>
                        </form>
                    </div>}
                    {/* Progress (auto-activate path) */}
                    {!need_details && <div className="">
                        {!err_msg && <Spinner height={200} />}
                    </div>}
                </div>
                {/* Footer */}
                <div className="flex flex-row justify-end">
                    <IodyneCloudIcon height={25} />
                </div>
            </div>
        </div> }
    </div>
}
